import type { BusinessCapability } from "@entur-partner/permission-client-node";

// Namespace every capability this app owns; used to tell ours from other micro-frontends'.
export const OPS_CAPABILITY_PREFIX = "automat-ops-api-";

// Capabilities mirror ops-api, where each machine field group is gated by its own
// capability. The API projects responses by the caller's LES access, so the UI gates
// what it SHOWS on the same capability the API checks. machine-metadata-general is held
// by every ops-ui user (support + admin); the technical/version tiers are admin-only.

export const ALARM_READ: BusinessCapability = {
  operation: "automat-ops-api-alarm",
  access: "les"
};

export const APP_VERSION_READ: BusinessCapability = {
  operation: "automat-ops-api-app-versions",
  access: "les"
};

export const APP_VERSION_DELETE: BusinessCapability = {
  operation: "automat-ops-api-app-versions",
  access: "slett"
};

// General machine fields (id/posId/location/displayName) — the baseline read for the
// overview and deployment pages.
export const MACHINE_READ: BusinessCapability = {
  operation: "automat-ops-api-machine-metadata-general",
  access: "les"
};

// Technical fields (hardwareId, timestamps): read shows them.
export const MACHINE_TECHNICAL_READ: BusinessCapability = {
  operation: "automat-ops-api-machine-metadata-technical",
  access: "les"
};

export const MACHINE_GENERAL_EDIT: BusinessCapability = {
  operation: "automat-ops-api-machine-metadata-general",
  access: "endre"
};

export const MACHINE_TECHNICAL_EDIT: BusinessCapability = {
  operation: "automat-ops-api-machine-metadata-technical",
  access: "endre"
};

// Editing machine metadata (PATCH /{id}) requires endre on both metadata capabilities,
// mirroring the API's combined gate — only admins hold both.
export const MACHINE_METADATA_EDIT_CAPABILITIES = [MACHINE_GENERAL_EDIT, MACHINE_TECHNICAL_EDIT];

export const MACHINE_CLOSED_READ: BusinessCapability = {
  operation: "automat-ops-api-machine-closed",
  access: "les"
};

// Close/reopen machines (PUT /closed).
export const MACHINE_CLOSED_EDIT: BusinessCapability = {
  operation: "automat-ops-api-machine-closed",
  access: "endre"
};

export const MACHINE_VERSIONS_READ: BusinessCapability = {
  operation: "automat-ops-api-machine-app-versions",
  access: "les"
};

// Assign app versions (PUT /versions).
export const MACHINE_VERSIONS_EDIT: BusinessCapability = {
  operation: "automat-ops-api-machine-app-versions",
  access: "endre"
};

// Notes are gated on their own capability only, not on machine-metadata-general.
export const MACHINE_NOTE_READ: BusinessCapability = {
  operation: "automat-ops-api-machine-note",
  access: "les"
};

export const MACHINE_NOTE_CREATE: BusinessCapability = {
  operation: "automat-ops-api-machine-note",
  access: "opprett"
};

export const MACHINE_CREATE: BusinessCapability = {
  operation: "automat-ops-api-machine",
  access: "opprett"
};

export const MACHINE_DELETE: BusinessCapability = {
  operation: "automat-ops-api-machine",
  access: "slett"
};

// Assigning versions needs the machine list, the version catalogue, and les on the
// version fields, which is what makes the API project them at all. Shared by the
// deployment page's menu item and route and the swap on machine detail.
export const DEPLOYMENT_CAPABILITIES = [MACHINE_READ, MACHINE_VERSIONS_READ, MACHINE_VERSIONS_EDIT, APP_VERSION_READ];

export const APP_VERSIONS_CAPABILITIES = [MACHINE_READ, MACHINE_VERSIONS_READ, APP_VERSION_READ, APP_VERSION_DELETE];

// Mirrors the Support grouping in automat-ops-api's application.yml.
export const SUPPORT_CAPABILITIES = [
  MACHINE_READ,
  MACHINE_CLOSED_READ,
  MACHINE_CLOSED_EDIT,
  ALARM_READ,
  MACHINE_NOTE_READ,
  MACHINE_NOTE_CREATE
];

// Capabilities application.yml marks "# Admins".
export const ADMIN_ONLY_CAPABILITIES = [
  APP_VERSION_DELETE,
  MACHINE_CREATE,
  MACHINE_DELETE,
  MACHINE_TECHNICAL_READ,
  MACHINE_TECHNICAL_EDIT,
  MACHINE_VERSIONS_EDIT
];
